import { cn } from '@/lib';
import { SocialPlatform } from '../types/profile.types';
import { getSocialPlatformConfig, PLATFORM_ICONS } from '../constants/socialPlatforms';

interface SocialInputProps {
    id: string;
    name: string;
    platform: SocialPlatform;
    value: string;
    placeholder?: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: React.FocusEventHandler<HTMLInputElement>;
    className?: string;
    error?: boolean;
    disabled?: boolean;
    required?: boolean;
    hintText?: string;
}

export function SocialInput({
    id,
    name,
    platform,
    value,
    placeholder,
    onChange,
    onBlur,
    className,
    error,
    disabled,
    required,
    hintText,
}: SocialInputProps) {
    const config = getSocialPlatformConfig(platform);
    const SvgIcon = PLATFORM_ICONS[platform];

    if (!config) {
        return null;
    }

    const effectivePlaceholder = placeholder || config.placeholder;

    return (
        <div className='flex flex-col gap-1.5'>
            <div
                className={cn(
                    'flex items-center gap-0 overflow-hidden rounded-md border transition-all duration-150',
                    'bg-canvas border-neutral-subtle',
                    'hover:border-brand-main',
                    'focus-within:border-brand-main focus-within:ring-brand-subtle focus-within:ring-4',
                    error && 'border-negative-main bg-negative-subtle',
                    error && 'hover:border-negative-main focus-within:border-negative-main',
                    error && 'focus-within:ring-0',
                    disabled && 'cursor-not-allowed opacity-50',
                    className
                )}
            >
                {/* Icon */}
                <div className='flex shrink-0 items-center justify-center pl-2'>
                    <SvgIcon className='size-5' />
                </div>

                {/* Hint Text */}
                <span
                    className={cn(
                        'text-label-xs-regular! flex w-10 items-center justify-center py-2 text-center tracking-tighter',
                        error && 'text-negative-main'
                    )}
                >
                    {config.hintLabel}
                </span>

                {/* Vertical Divider */}
                <div className='bg-semantic-fg-subtle h-6 w-px shrink-0' />

                {/* Input */}
                <input
                    id={id}
                    name={name}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder={effectivePlaceholder}
                    disabled={disabled}
                    required={required}
                    className={cn(
                        'text-body-md flex-1 border-0 bg-transparent px-3 py-2.5 outline-none',
                        'placeholder:text-gray',
                        'disabled:cursor-not-allowed disabled:opacity-50',
                        error && 'bg-negative-subtle'
                    )}
                />
            </div>

            {/* Error/Hint Text */}
            {hintText && (
                <p
                    className={cn(
                        'text-body-xs! px-1.5 font-semibold!',
                        error ? 'text-negative-fg-negative-bold' : 'text-gray'
                    )}
                >
                    {hintText}
                </p>
            )}
        </div>
    );
}
